import { Head, Link, router } from "@inertiajs/react"
import {
  Check,
  Edit,
  Lock,
  Minus,
  Trash2,
  Users as UsersIcon,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { adminRolesPath } from "@/routes"
import type { AdminUser, BreadcrumbItem, PermissionCatalog } from "@/types"

interface Props {
  role: {
    id: number
    name: string
    description: string | null
    system: boolean
    permissions: string[]
    user_count: number
  }
  available_permissions: PermissionCatalog
  users: AdminUser[]
  can: { update: boolean; destroy: boolean }
}

export default function Show({
  role,
  available_permissions,
  users,
  can,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Roles", href: adminRolesPath() },
    { title: role.name, href: `/admin/roles/${role.id}` },
  ]

  const held = new Set(role.permissions)
  const totalKeys = Object.values(available_permissions)
    .flatMap((groups) => Object.values(groups))
    .reduce((count, group) => count + Object.keys(group).length, 0)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={role.name} />

      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold capitalize">{role.name}</h1>
              {role.system && (
                <Badge variant="outline" className="gap-1">
                  <Lock className="h-3 w-3" /> Built-in
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground text-sm">
              {role.description ?? "No description."}
            </p>
            <p className="text-muted-foreground text-sm">
              {held.size} of {totalKeys} permissions · {role.user_count}{" "}
              {role.user_count === 1 ? "user" : "users"}
            </p>
          </div>

          <div className="flex gap-2">
            {can.update && (
              <Button render={<Link href={`/admin/roles/${role.id}/edit`} />}>
                <Edit className="mr-2 h-4 w-4" /> Edit Permissions
              </Button>
            )}
            {can.destroy && (
              <Button
                variant="outline"
                onClick={() => {
                  if (confirm(`Delete the ${role.name} role?`)) {
                    router.delete(`/admin/roles/${role.id}`)
                  }
                }}
              >
                <Trash2 className="text-destructive mr-2 h-4 w-4" /> Delete
              </Button>
            )}
          </div>
        </div>

        {role.system && (
          <p className="text-muted-foreground text-sm">
            The app refers to this role by name, so it can&apos;t be renamed or
            deleted. Its permissions are yours to change.
          </p>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Permissions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            {Object.entries(available_permissions).map(([category, groups]) => {
              const keys = Object.values(groups).flatMap((group) =>
                Object.keys(group),
              )
              const granted = keys.filter((key) => held.has(key)).length

              return (
                <section key={category} className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">{category}</h3>
                    <Badge variant={granted === 0 ? "outline" : "secondary"}>
                      {granted}/{keys.length}
                    </Badge>
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    {Object.entries(groups).map(([group, permissions]) => (
                      <div key={group} className="space-y-2">
                        <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                          {group}
                        </p>
                        {Object.entries(permissions).map(([key, label]) => {
                          const on = held.has(key)
                          return (
                            <div
                              key={key}
                              className="flex items-start gap-2 text-sm"
                            >
                              {on ? (
                                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                              ) : (
                                <Minus className="text-muted-foreground/50 mt-0.5 h-4 w-4 shrink-0" />
                              )}
                              <span
                                className={
                                  on ? undefined : "text-muted-foreground/60"
                                }
                              >
                                {label}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    ))}
                  </div>

                  <Separator />
                </section>
              )
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <UsersIcon className="h-4 w-4" /> Users with this role (
              {users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Nobody holds this role
                {can.destroy ? " — it can be deleted." : " yet."}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow
                      key={user.id}
                      className={user.archived ? "opacity-60" : undefined}
                    >
                      <TableCell>
                        <Link
                          href={`/admin/users/${user.id}`}
                          className="font-medium hover:underline"
                        >
                          {user.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {user.email}
                      </TableCell>
                      <TableCell>
                        {user.archived ? (
                          <Badge variant="outline">Archived</Badge>
                        ) : user.verified ? (
                          <Badge variant="secondary">Active</Badge>
                        ) : (
                          <Badge variant="destructive">Unverified</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
